import { CheckIcon } from "@heroicons/react/16/solid";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { twMerge } from "tailwind-merge";

import { Label } from "@fronterahealth/frontera-ui-components";

import { ReportTargetType, useUpdateTargetExcludeFromReportMutation } from "@api/graphql/types-and-hooks";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@components/ContextMenu";
import { HighlightedText } from "@components/HighlightedText/HighlightedText";
import { MenuItem } from "@components/Menu";
import { notifyError } from "@components/notifications/notifications";
import { GoalBadge } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalBadge";
import { useReportProgrammingData } from "@providers/ReportProgrammingProvider";
import { useReport } from "@providers/ReportProvider";

interface TargetRowProps {
  target: ReportTargetType;
  isSelected: boolean;
  onClick: () => void;
  /** Term to highlight in the target name */
  highlightTerm?: string;
  /** Parent goal name to display as subtitle */
  goalName?: string;
  className?: string;
  /** Optional identifier prefix (e.g. "1A. ") rendered before the name. */
  identifierPrefix?: string;
}

export const TargetRow: React.FC<TargetRowProps> = ({
  target,
  isSelected,
  onClick,
  highlightTerm,
  goalName,
  className,
  identifierPrefix,
}) => {
  const { dispatchSelected, selected } = useReportProgrammingData();
  const { report } = useReport();
  const queryClient = useQueryClient();
  const reportId = report.id;
  const { mutate: excludeTargetMutation } = useUpdateTargetExcludeFromReportMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals", reportId] });
      if (selected.mode === "target" && selected.targetId === target.id) {
        dispatchSelected({ type: "CLEAR" });
      }
    },
    onError: () => notifyError("Failed to remove target from report"),
  });

  const handleActivate = () => {
    onClick();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      handleActivate();
    }
  };
  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div
          className={twMerge(
            `flex min-h-8 w-full items-center gap-3 py-0.5 pl-9 pr-2`,
            isSelected ? "rounded-sm bg-limestone-200 hover:bg-limestone-200" : "hover:bg-limestone-50",
            className,
          )}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            handleActivate();
          }}
          onKeyDown={handleKeyDown}
          title={target.targetName || "Untitled Target"}
        >
          <div className="flex min-w-0 flex-col flex-1 items-start gap-0.5">
            <Label
              size="small"
              displayType={isSelected ? "loud" : "normal"}
              className="w-full truncate text-left ph-no-capture"
              colorType={isSelected ? "primary" : "secondary"}
            >
              {identifierPrefix && <span className="text-text-tertiary">{identifierPrefix}</span>}
              <HighlightedText text={target.targetName || "Untitled Target"} highlightTerm={highlightTerm} />
            </Label>
            {goalName && (
              <Label size="extra-small" colorType="tertiary" className="ph-no-capture">
                {goalName}
              </Label>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <GoalBadge status={target.status} />
            <CheckIcon
              className={twMerge("h-4 w-4", target.summaryReviewed ? "text-green-700" : "text-limestone-400")}
            />
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {({ close }) => (
          <MenuItem
            onClick={() => {
              excludeTargetMutation({
                input: { targetId: target.id, excludeFromReport: true },
              });
              close();
            }}
          >
            Remove from report
          </MenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};
